import {
  ApplicationConfig,
  APP_INITIALIZER,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { initializeApp, provideFirebaseApp, getApp } from '@angular/fire/app';
import { getAuth, provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  provideAppCheck,
} from '@angular/fire/app-check';
import { getFirestore, provideFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { getDatabase, provideDatabase, connectDatabaseEmulator } from '@angular/fire/database';
import { getFunctions, provideFunctions, connectFunctionsEmulator } from '@angular/fire/functions';
import { getStorage, provideStorage, connectStorageEmulator } from '@angular/fire/storage';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { environment } from '../environments/environment';
import { TenantContextService } from '@pos-shared';
import { initializeApp as initializeAppState } from './core/initializers/app.initializer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' })
    ),
    provideAnimationsAsync(),
    provideHttpClient(),
    importProvidersFrom([
      SweetAlert2Module.forRoot(), // ngx-sweetalert2: https://github.com/sweetalert2/ngx-sweetalert2
      TranslateModule.forRoot({
        defaultLanguage: 'de'
      })
    ]),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }),
    // Firebase App Initialization mit Environment-Konfiguration
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAppCheck(() => {
      // Debug Token setzen VOR App Check Initialisierung (wenn konfiguriert)
      if (environment.appCheck?.debugToken) {
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheck.debugToken;
      }

      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaEnterpriseProvider(environment.recaptchaSiteKey),
        isTokenAutoRefreshEnabled: true
      });
    }),

    // Auth mit Emulator-Support
    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators && (environment as any).emulators?.auth) {
        connectAuthEmulator(auth, (environment as any).emulators.auth.url, {
          disableWarnings: true
        });
      }
      return auth;
    }),

    // Firestore mit Emulator-Support
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.useEmulators && (environment as any).emulators?.firestore) {
        try {
          // Prüfe ob Emulator bereits verbunden ist
          // @ts-ignore - _settings ist internal
          const settings = firestore._settings;
          if (!settings.host || !settings.host.includes('127.0.0.1')) {
            console.log('🔥 Connecting to Firestore Emulator:',
              (environment as any).emulators.firestore.host,
              (environment as any).emulators.firestore.port
            );
            connectFirestoreEmulator(
              firestore,
              (environment as any).emulators.firestore.host,
              (environment as any).emulators.firestore.port
            );
            console.log('✅ Firestore Emulator connected!');
          } else {
            console.log('ℹ️ Firestore Emulator already connected');
          }
        } catch (error: any) {
          console.error('❌ Error connecting to Firestore Emulator:', error);
          // Ignoriere Fehler wenn bereits verbunden
          if (!error?.message?.includes('already been called')) {
            throw error;
          }
        }
      } else {
        console.log('⚠️ Production mode - connecting to real Firestore');
      }
      return firestore;
    }),

    // Realtime Database mit Emulator-Support
    provideDatabase(() => {
      const database = getDatabase();
      if (environment.useEmulators && (environment as any).emulators?.database) {
        connectDatabaseEmulator(
          database,
          (environment as any).emulators.database.host,
          (environment as any).emulators.database.port
        );
      }
      return database;
    }),

    // Cloud Functions mit Emulator-Support und europe-west3 Region
    provideFunctions(() => {
      const functions = getFunctions(getApp(), 'europe-west3');
      if (environment.useEmulators && (environment as any).emulators?.functions) {
        connectFunctionsEmulator(
          functions,
          (environment as any).emulators.functions.host,
          (environment as any).emulators.functions.port
        );
      }
      return functions;
    }),

    // Storage mit Emulator-Support
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators && (environment as any).emulators?.storage) {
        connectStorageEmulator(
          storage,
          (environment as any).emulators.storage.host,
          (environment as any).emulators.storage.port
        );
      }
      return storage;
    }),

    // Performance Monitoring
    providePerformance(() => getPerformance()),

    // App Initializer: Läuft VOR Router-Navigation
    // Stellt sicher, dass Firebase Auth und TenantContext geladen sind
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppState,
      multi: true
    },

  ],
};
